import { Analytics } from '../../../modules/analytics/analytics';
import { autoInjectable, container } from 'tsyringe';
import { SSRTextPlacementContent } from '@ads-ep/fetch-api-client';
import { PlacementConfig } from '../../../models/placement-config.model';
import { PlacementMetadata } from '../../../models/placement-meta-data.model';
import { CommonPlacement } from '../../common-placement/common-placement.component';
import { fromEvent, merge, Subject } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { placementActionReducer } from '../../../actions/placement-action-reducer';
import { EpStore } from '../../../services/store/ep-store';
import { ComponentGenerator } from '../../../services/component-generator/component-generator.service';
import {
  FmcSdk
} from 'fmc-sdk';
import { PublicEventEmitterService } from '../../../services/public-event-emitter/public-event-emitter.service';
import { AnyAction } from 'deox';
import {
  bindEmbeddedFrameEvents,
  getSdkMessageCallback,
  unsubscribeEmbeddedFrameEvents
} from '../../../util/fmc-helpers/fmc-helpers.util';
import { OverlayActions, PluginActions } from '../../../actions';

/*
 * This is basically an abstract class and not meant to be
 * instantiated directly. Each placement extends this base
 * component.
 * */
@autoInjectable()
export class SsrOverlayComponent extends CommonPlacement {
  overlayClass = 'base-overlay';
  componentGenerator: ComponentGenerator;
  onDispose$ = new Subject();
  clickTargetSelector = '[data-epjs-overlay-click]';
  placementConfig: PlacementConfig = new PlacementConfig();
  fmcSdk: FmcSdk;
  sdkCallbackFnc: (action: AnyAction) => void;
  overlayDisposeLock = false;

  /*
  * Dom element for instance of this component.
  * The template will render into the innerHTML
  * of this dom element.
  * */
  containerEl: HTMLElement;

  /*
  * Most things could be done in the constructor,
  * but keeping the constructor small to make it easier
  * to test functions independently and handle dependency
  * injection. Also, empty so any classes that extend this
  * one can override the init function instead of the constructor.
  * */
  constructor(
    public placementContent: SSRTextPlacementContent,
    public originPlacementConfig?: PlacementConfig,
    public originMetaData?: PlacementMetadata,
    private analytics?: Analytics,
    private store?: EpStore,
  ) {
    super();
    this.componentGenerator = container.resolve(ComponentGenerator);
    this.placementConfig.content = { ...placementContent };

    /*
    * TODO Decide how to handle missing required placementContent
    * */
    if (!placementContent) {
      return;
    }
  }

  /*
  * Make any analyticsClient calls and clean up dom
  * */
  dispose() {
    this.onDispose$.next();
    unsubscribeEmbeddedFrameEvents(this.fmcSdk, this.sdkCallbackFnc);
  }

  /*
  * Set true to prompt user with confirm dialog before
  * dismissing overlay
  * */
  setOverlayDisposeLock(locked: boolean){
    this.overlayDisposeLock = locked;
  }

  /*
  * Maybe change to getOrCreate?
  * */
  getDomElement(): HTMLElement {
    return this.containerEl;
  }

  /*
   * Run after component is created. Code to bind
   * the component to the DOM and add event handlers
   * should go here.
   * */
  async init() {
    this.containerEl = document.createElement('div');
    this.containerEl.className = `epjs-css-overlay ${this.overlayClass}`;
    this.containerEl.innerHTML = this.placementContent.contentData.htmlContent;

    await this.fetchPlacements(this.containerEl);
    this.bindActions(this.containerEl);

    const bindFrameEventsResp = bindEmbeddedFrameEvents(this.containerEl, this.store);
    if(!!bindFrameEventsResp){
      this.sdkCallbackFnc = bindFrameEventsResp.sdkMessageCallback;
      this.fmcSdk = bindFrameEventsResp.fmcSdkInstance;
      /*
      * If the bindFrameEventsResp exists, there is an iframe
      * and the dispose lock should be set to true.
      * */
      this.setOverlayDisposeLock(true);
    }

    this.addEventListeners();
  }

  /*
  * Add any new event listeners here
  * for the overriding component
  * */
  addEventListeners() {

  }

  /*
 * Parse injected dom for data-action-id attributes.
 * If found, bind a click handler to them to call performPlacementAction
 * */
  bindActions(domTarget: HTMLElement) {
    /*
    * Should there be a flag or check to ensure listeners aren't added twice?
    * */
    const actionTargets = domTarget.querySelectorAll([
      this.actionQuerySelector,
      this.clickTargetSelector
    ].join(','));

    if (actionTargets.length === 0) {
      return;
    }

    this.clickActions$ = fromEvent<MouseEvent>(actionTargets, 'click');
    this.enterKeyActions$ = fromEvent<KeyboardEvent>(actionTargets, 'keyup').pipe(filter((event) => event.code === 'Enter'));
    this.placementActions$ = merge(this.clickActions$,this.enterKeyActions$);

    /*
    * Actions with type set in data attribute
    * */
    this.placementActions$.pipe(
      map(e => e.currentTarget),
      filter((domEl: HTMLElement) => !!domEl.dataset.actionType),
      map(domEl => placementActionReducer(
        domEl.dataset.actionType,
        domEl.dataset,
        this
      )),
      filter(action => !!action),
      tap(action => this.store.actions$.next(action))
    ).subscribe();
  }

  /*
  * Loop through each placement that has the attribute "fetch" === true.
  * - Fetch each found placement from server
  * - For each placement object returned
  * - - update the corresponding dom element with a content id
  * */
  async fetchPlacements(domTarget: HTMLElement) {
    /* tslint:disable:no-useless-cast */
    const fetchTargets =
      Array.from(domTarget.querySelectorAll(this.fetchQuerySelector())) as HTMLElement[];

    if (fetchTargets.length === 0) {
      return;
    }

    const placementConfigs = await this.componentGenerator.fetchPlacements(fetchTargets, 'contentFetch');
    this.brandPresentmentConfig = placementConfigs;

    /*
    * Loop through returned placements and update corresponding dom elements
    * */
    placementConfigs.placements.forEach(placement => {
      const updateTargets = domTarget.querySelectorAll(this.fetchQuerySelector(placement.id));
      updateTargets.forEach((target: HTMLElement) =>
        target.dataset.actionContentId = placement.content.contentId
      );
    });
  }

  addedToDom() {
    super.addedToDom();
    this.store.dispatch(PluginActions.contentAddedToDom({domTarget: this.containerEl}));
  }
}
